"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence, useInView } from "framer-motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ShieldCheck,
  ScanLine,
  BellRing,
  Truck,
  Users,
  Search,
  CheckCircle2,
  Phone,
  ArrowRight,
  Clock,
  LucideIcon,
  X,
  PhoneCall,
} from "lucide-react";

// Register GSAP Plugin
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// --- TYPES ---
interface Feature {
  id: string;
  step: string;
  tab: string;
  title: string;
  heading: string;
  desc: string;
  icon: LucideIcon;
}

interface FeatureStepProps {
  feature: Feature;
  index: number;
  isActive: boolean;
  onActive: () => void;
  renderMobileVisual: () => React.ReactNode;
}

interface ScreenProps {
  wrapperClass: string;
  paddingTop: string;
}

// --- FEATURE DATA ---
const FEATURES: Feature[] = [
  {
    id: "guest-entry",
    step: "01",
    tab: "Guest Entry",
    title: "10-Second Guest Entry",
    heading: "No Register. Just Scan.",
    desc: "Guards capture visitor details or scan a pre-approved QR code in seconds. No more illegible handwriting in registers.",
    icon: ScanLine,
  },
  {
    id: "approval-buzz",
    step: "02",
    tab: "Approval Buzz",
    title: "Real-Time Approval",
    heading: "The 'Buzz' Escalation.",
    desc: "Instant lock-screen approval. If a resident doesn't respond, the system auto-escalates: Notification → Buzz → Critical Alarm.",
    icon: BellRing,
  },
  {
    id: "quick-delivery",
    step: "03",
    tab: "Quick Delivery",
    title: "3-Tap Delivery",
    heading: "Zero-Friction Logistics.",
    desc: "Guards select the brand (Zomato/Swiggy) and tap the flat number. Entry logged and approved in under 3 seconds.",
    icon: Truck,
  },
  {
    id: "staff-attendance",
    step: "04",
    tab: "Staff Tracking",
    title: "Staff Attendance",
    heading: "Automated In/Out Logs.",
    desc: "Maids and drivers have dedicated profiles. Guards simply toggle 'In' or 'Out' to track daily attendance accurately.",
    icon: Users,
  },
  {
    id: "vehicle-search",
    step: "05",
    tab: "Vehicle Search",
    title: "Vehicle Security",
    heading: "Instant Owner Lookup.",
    desc: "Wrong parking? Security can search any vehicle number to instantly find the owner and call them directly.",
    icon: Search,
  },
];

const GateKeepingSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Refs for Auto-Scrolling Nav
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // --- GSAP ANIMATIONS (Section Entrance) ---
  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%", // Trigger when section is 20% into viewport
          toggleActions: "play none none reverse",
        },
      });

      tl.from(headerRef.current, {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
      }).from(
        ".nav-bar-stagger",
        {
          y: -20,
          opacity: 0,
          duration: 0.6,
          ease: "back.out(1.7)",
        },
        "-=0.5",
      );
    },
    { scope: containerRef },
  );

  // Mobile/Desktop Nav Auto-Scroll Logic
  useEffect(() => {
    if (navContainerRef.current && navButtonRefs.current[activeStep]) {
      const container = navContainerRef.current;
      const button = navButtonRefs.current[activeStep];

      if (button) {
        const containerWidth = container.offsetWidth;
        const buttonLeft = button.offsetLeft;
        const buttonWidth = button.offsetWidth;
        const scrollPos = buttonLeft - containerWidth / 2 + buttonWidth / 2;

        container.scrollTo({
          left: scrollPos,
          behavior: "smooth",
        });
      }
    }
  }, [activeStep]);

  // Manual Scroll to Section
  const scrollToStep = (index: number) => {
    const element = document.getElementById(`feature-${index}`);
    if (element) {
      const offset = 158;
      const y = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      ref={containerRef}
      id="gate-security"
      className="relative bg-[#050505] pt-24 pb-32 lg:pb-48"
    >
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1200px] bg-[radial-gradient(ellipse_at_top,var(--tw-gradient-stops))] from-orange-900/10 via-[#050505] to-[#050505]" />
      </div>

      <div className="relative z-10">
        {/* --- HEADER --- */}
        <div
          ref={headerRef}
          className="max-w-4xl mx-auto text-center mb-16 px-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-widest mb-6 backdrop-blur-md">
            <ShieldCheck className="w-3 h-3" /> Gate Security Suite
          </div>
          <h2 className="text-4xl md:text-6xl font-clash font-bold text-white mb-6 leading-tight">
            Ironclad Security. <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
              Zero Friction.
            </span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
            From 10-second deliveries to airtight visitor verification. The
            complete gate OS.
          </p>
        </div>

        {/* --- STICKY NAVIGATION BAR --- */}
        <div className="sticky top-[77px] z-50 mb-16 lg:mb-24 px-4 flex justify-center pointer-events-none nav-bar-stagger">
          <div
            ref={navContainerRef}
            className="pointer-events-auto max-w-full overflow-x-auto no-scrollbar pb-2"
          >
            <motion.div className="flex items-center gap-1 bg-black/80 backdrop-blur-xl border border-white/15 rounded-full p-1 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] ring-1 ring-white/5 w-max mx-auto h-[41px] max-w-[900px]">
              {FEATURES.map((feature, index) => (
                <button
                  key={feature.id}
                  ref={(el) => {
                    navButtonRefs.current[index] = el;
                  }}
                  onClick={() => {
                    setActiveStep(index);
                    scrollToStep(index);
                  }}
                  className={`relative flex items-center gap-2 px-3 lg:px-4 h-full rounded-full text-[11px] lg:text-xs font-bold transition-all duration-300 ${
                    activeStep === index
                      ? "text-white"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {activeStep === index && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-white/10 border border-white/5 rounded-full shadow-[0_0_15px_rgba(255,255,255,0.1)]"
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                  <feature.icon
                    className={`w-3.5 h-3.5 ${activeStep === index ? "text-orange-500" : "opacity-70"}`}
                  />
                  <span className="relative z-10 whitespace-nowrap">
                    {feature.tab}
                  </span>
                </button>
              ))}
            </motion.div>
          </div>
        </div>

        {/* --- MAIN CONTENT --- */}
        <div className="max-w-7xl mx-auto relative lg:flex gap-24 px-6 md:px-12">
          {/* --- LEFT: SCROLLABLE NARRATIVE --- */}
          <div className="w-full lg:w-[45%] flex flex-col relative z-10">
            {FEATURES.map((feature, index) => (
              <FeatureStep
                key={feature.id}
                feature={feature}
                index={index}
                isActive={activeStep === index}
                onActive={() => setActiveStep(index)}
                renderMobileVisual={() => (
                  // MOBILE VISUAL CONTAINER
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="lg:hidden w-full aspect-[4/5] sm:aspect-video mb-8 bg-[#080808] rounded-3xl border border-white/10 overflow-hidden relative shadow-2xl ring-1 ring-white/5"
                  >
                    <ScreenContent stepIndex={index} isMobile={true} />

                    {/* Interactive Hint */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full flex items-center gap-1.5 pointer-events-none">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                      <span className="text-[9px] font-bold text-white/70 uppercase tracking-wider">
                        Interactive Demo
                      </span>
                    </div>
                  </motion.div>
                )}
              />
            ))}
          </div>

          {/* --- RIGHT: PINNED PHONE (Desktop) --- */}
          <div className="hidden lg:block w-[55%] relative">
            <div className="sticky top-[150px] h-[750px] flex items-start justify-center">
              <div className="relative w-[380px] h-[750px] bg-[#050505] rounded-[3.5rem] border-[8px] border-[#151515] shadow-[0_0_60px_-15px_rgba(249,115,22,0.15)] overflow-hidden ring-1 ring-white/10 z-20">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-36 h-8 bg-[#151515] rounded-b-2xl z-50 flex items-center justify-center gap-3 shadow-lg">
                  <div className="w-12 h-1.5 rounded-full bg-[#2a2a2a]" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#333]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent pointer-events-none z-40" />

                <div className="w-full h-full bg-[#080808] relative overflow-hidden font-sans text-white">
                  <AnimatePresence mode="wait">
                    <ScreenContent stepIndex={activeStep} isMobile={false} />
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureStep: React.FC<FeatureStepProps> = ({
  feature,
  index,
  isActive,
  onActive,
  renderMobileVisual,
}) => {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { margin: "-45% 0px -45% 0px" });

  useEffect(() => {
    if (isInView) onActive();
  }, [isInView, onActive]);

  return (
    <article
      id={`feature-${index}`}
      ref={ref}
      className="relative flex flex-col justify-center py-12 lg:py-0 lg:min-h-[85vh] pl-0 lg:pl-8"
    >
      {renderMobileVisual()}

      <motion.div
        animate={{
          opacity: isActive ? 1 : 0.2,
          filter: isActive ? "blur(0px)" : "blur(2px)",
        }}
        transition={{ duration: 0.5 }}
        className="relative"
      >
        <div className="hidden lg:block absolute left-full top-1/2 -translate-y-1/2 w-[20vw] h-[2px] pointer-events-none overflow-hidden z-0">
          <AnimatePresence>
            {isActive && (
              <motion.div
                initial={{ x: "-100%", opacity: 0 }}
                animate={{ x: "0%", opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ duration: 0.8, ease: "anticipate" }}
                className="w-full h-full bg-gradient-to-r from-orange-500 via-orange-500/50 to-transparent"
              />
            )}
          </AnimatePresence>
        </div>

        <div className="relative z-10">
          <motion.div
            className="flex items-center gap-4 mb-4 lg:mb-6"
            initial={false}
            animate={{ x: isActive ? 0 : -20 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <span
              className={`text-sm font-mono font-bold px-2 py-1 rounded border transition-colors duration-500 ${
                isActive
                  ? "text-orange-500 border-orange-500/50 bg-orange-500/10"
                  : "text-gray-600 border-white/5 bg-transparent"
              }`}
            >
              {feature.step}
            </span>
            <h3 className="text-2xl lg:text-3xl font-bold text-white">
              {feature.title}
            </h3>
          </motion.div>

          <motion.p
            className="text-gray-300 lg:text-gray-400 text-lg lg:text-lg leading-relaxed mb-8 max-w-md"
            initial={false}
            animate={{ y: isActive ? 0 : 10 }}
            transition={{ duration: 0.4 }}
          >
            {feature.desc}
          </motion.p>
        </div>
      </motion.div>
    </article>
  );
};

// --- INTERACTIVE SCREEN CONTENT ---
const ScreenContent = ({
  stepIndex,
  isMobile,
}: {
  stepIndex: number;
  isMobile: boolean;
}) => {
  const wrapperClass = "h-full flex flex-col bg-[#080808] relative";
  const paddingTop = isMobile ? "pt-6" : "pt-16";

  const renderContent = () => {
    switch (stepIndex) {
      case 0:
        return (
          <GuestScreen wrapperClass={wrapperClass} paddingTop={paddingTop} />
        );
      case 1:
        return (
          <BuzzScreen wrapperClass={wrapperClass} paddingTop={paddingTop} />
        );
      case 2:
        return (
          <DeliveryScreen wrapperClass={wrapperClass} paddingTop={paddingTop} />
        );
      case 3:
        return (
          <StaffScreen wrapperClass={wrapperClass} paddingTop={paddingTop} />
        );
      case 4:
        return (
          <VehicleScreen wrapperClass={wrapperClass} paddingTop={paddingTop} />
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      key={stepIndex}
      initial={{ opacity: 0, y: 20, filter: "blur(5px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -20, filter: "blur(5px)" }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="h-full w-full"
    >
      {renderContent()}
    </motion.div>
  );
};

// 1. GUEST ENTRY SCREEN
const GuestScreen: React.FC<ScreenProps> = ({ wrapperClass, paddingTop }) => {
  const [visitors, setVisitors] = useState([
    {
      name: "Rahul Sharma",
      type: "Owner",
      flat: "A-101",
      status: "Active",
      time: "Just now",
      color: "text-green-400",
      bg: "bg-green-500/10",
    },
    {
      name: "Zomato",
      type: "Delivery",
      flat: "B-204",
      status: "Verified",
      time: "2m ago",
      color: "text-blue-400",
      bg: "bg-blue-500/10",
    },
    {
      name: "Priya Desai",
      type: "Tenant",
      flat: "A-102",
      status: "Invited",
      time: "1h ago",
      color: "text-purple-400",
      bg: "bg-purple-500/10",
    },
  ]);

  const addVisitor = () => {
    const newVisitor = {
      name: "New Visitor",
      type: "Guest",
      flat: "C-301",
      status: "Scanning...",
      time: "Now",
      color: "text-orange-400",
      bg: "bg-orange-500/10",
    };
    setVisitors([newVisitor, ...visitors]);
    setTimeout(() => {
      setVisitors((prev) => {
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          name: "Amit Verma",
          status: "Verified",
          color: "text-green-400",
          bg: "bg-green-500/10",
        };
        return updated;
      });
    }, 1500);
  };

  return (
    <div className={`${wrapperClass} ${paddingTop}`}>
      <div className="px-6 mb-6 flex justify-between items-end">
        <div>
          <h4 className="font-bold text-2xl text-white">Gate Entry</h4>
          <p className="text-gray-500 text-xs uppercase tracking-wider">
            Live Log
          </p>
        </div>
        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
          <Clock className="w-4 h-4 text-gray-400" />
        </div>
      </div>
      <div className="px-6 mb-4">
        <div className="bg-[#151515] border border-white/10 rounded-xl p-3 flex items-center gap-3 text-gray-500">
          <Search className="w-4 h-4" />
          <span className="text-sm">Search visitor...</span>
        </div>
      </div>
      <div className="flex-1 px-4 space-y-3 relative overflow-hidden">
        <AnimatePresence initial={false}>
          {visitors.map((item, i) => (
            <motion.div
              key={item.name + i} // Unique key for animation
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              layout
              transition={{ duration: 0.3 }}
              className="bg-[#121212] p-4 rounded-2xl border border-white/5 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-xs font-bold text-gray-400 border border-white/5">
                  {item.flat}
                </div>
                <div>
                  <h5 className="font-bold text-sm text-white">{item.name}</h5>
                  <p className="text-[10px] text-gray-500 uppercase font-medium">
                    {item.type} • {item.time}
                  </p>
                </div>
              </div>
              <span
                className={`text-[10px] font-bold px-2.5 py-1 rounded-md ${item.bg} ${item.color}`}
              >
                {item.status}
              </span>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* INTERACTIVE BUTTON */}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={addVisitor}
          className="absolute bottom-6 right-6 w-14 h-14 bg-orange-600 rounded-full flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(234,88,12,0.5)] text-white z-10"
        >
          <ScanLine className="w-6 h-6" />
        </motion.button>
      </div>
    </div>
  );
};

// 2. BUZZ SCREEN
const BuzzScreen: React.FC<ScreenProps> = ({ wrapperClass, paddingTop }) => {
  const [status, setStatus] = useState<"pending" | "approved" | "denied">(
    "pending",
  );

  return (
    <div className="h-full flex flex-col items-center justify-center relative overflow-hidden">
      <Image
        src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1000&auto=format&fit=crop"
        alt="Abstract gradient background"
        fill
        className="object-cover absolute inset-0 z-0"
        sizes="100vw"
      />
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

      <AnimatePresence mode="wait">
        {status === "pending" ? (
          <motion.div
            key="alert"
            initial={{ y: 50, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", bounce: 0.4 }}
            className="relative z-20 w-[90%] bg-white/10 backdrop-blur-xl border border-white/20 p-5 rounded-[2rem] shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-600 rounded-[6px] flex items-center justify-center">
                  <ShieldCheck className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-bold text-white/90 tracking-wide">
                  SMART DWARA
                </span>
              </div>
              <span className="text-[10px] text-white/60">NOW</span>
            </div>
            <div>
              <h4 className="font-bold text-white mb-1">
                Visitor Request: Amit Verma
              </h4>
              <p className="text-sm text-gray-200 leading-snug mb-5">
                Standing at Gate 1. Entry code #9283.
                <br />
                Approve entry?
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setStatus("denied")}
                className="py-3.5 bg-white/10 hover:bg-white/20 rounded-2xl text-sm font-bold text-white backdrop-blur-md border border-white/5"
              >
                Decline
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setStatus("approved")}
                className="py-3.5 bg-white text-black hover:bg-gray-200 rounded-2xl text-sm font-bold shadow-lg"
              >
                Approve
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative z-20 flex flex-col items-center gap-4"
          >
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center ${status === "approved" ? "bg-green-500" : "bg-red-500"} shadow-2xl`}
            >
              {status === "approved" ? (
                <CheckCircle2 className="w-10 h-10 text-white" />
              ) : (
                <X className="w-10 h-10 text-white" />
              )}
            </div>
            <h3 className="text-2xl font-bold text-white">
              {status === "approved" ? "Access Granted" : "Entry Denied"}
            </h3>
            <button
              onClick={() => setStatus("pending")}
              className="text-xs text-white/50 underline"
            >
              Reset Demo
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 3. DELIVERY SCREEN
const DeliveryScreen: React.FC<ScreenProps> = ({
  wrapperClass,
  paddingTop,
}) => {
  const [log, setLog] = useState<{ name: string; time: string } | null>(null);

  const handleBrandClick = (name: string) => {
    setLog({ name, time: "Just now" });
  };

  return (
    <div className={`${wrapperClass} ${paddingTop} px-6`}>
      <div className="mb-6 lg:mb-8">
        <h4 className="font-bold text-2xl mb-1 text-white">Quick Delivery</h4>
        <p className="text-gray-500 text-xs">Tap brand to auto-log</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:gap-4">
        {[
          { name: "Zomato", bg: "bg-[#E23744]", text: "text-white" },
          { name: "Swiggy", bg: "bg-[#FC8019]", text: "text-white" },
          { name: "Amazon", bg: "bg-[#232F3E]", text: "text-white" },
          { name: "Blinkit", bg: "bg-[#F8CB46]", text: "text-black" },
          { name: "Zepto", bg: "bg-[#4B1884]", text: "text-white" },
          {
            name: "Uber",
            bg: "bg-[#000000] border border-white/20",
            text: "text-white",
          },
        ].map((brand, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleBrandClick(brand.name)}
            className="aspect-[4/3] rounded-2xl bg-[#121212] border border-white/5 flex flex-col items-center justify-center gap-3 hover:bg-[#1a1a1a] transition-colors cursor-pointer"
          >
            <div
              className={`w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-lg ${brand.bg} ${brand.text}`}
            >
              {brand.name[0]}
            </div>
            <span className="text-xs font-medium text-gray-400">
              {brand.name}
            </span>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {log && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="mt-6 bg-[#1a1a1a] p-4 rounded-xl border border-white/10 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm font-bold text-white">
                  {log.name} Logged
                </p>
                <p className="text-[10px] text-gray-500">
                  Auto-approved • {log.time}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 4. STAFF SCREEN
const StaffScreen: React.FC<ScreenProps> = ({ wrapperClass, paddingTop }) => {
  const [staffList, setStaffList] = useState([
    { id: 1, name: "Sunita Devi", role: "Maid", img: "S", status: "IN" },
    { id: 2, name: "Ramesh P.", role: "Driver", img: "R", status: "OUT" },
    { id: 3, name: "Kamla Bai", role: "Cook", img: "K", status: "IN" },
  ]);

  const toggleStatus = (id: number) => {
    setStaffList((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: s.status === "IN" ? "OUT" : "IN" } : s,
      ),
    );
  };

  return (
    <div className={`${wrapperClass} ${paddingTop} px-6`}>
      <div className="flex justify-between items-end mb-6 lg:mb-8">
        <div>
          <h4 className="font-bold text-2xl text-white">Daily Help</h4>
          <p className="text-gray-500 text-xs">Attendance Tracker</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black text-green-500">
            {staffList.filter((s) => s.status === "IN").length}
          </p>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
            Inside
          </p>
        </div>
      </div>

      <div className="space-y-3 lg:space-y-4">
        {staffList.map((staff, i) => (
          <motion.div
            key={staff.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="flex items-center justify-between p-3 lg:p-4 rounded-2xl bg-[#121212] border border-white/5"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#1a1a1a] flex items-center justify-center text-sm font-bold text-gray-300 border border-white/5">
                {staff.img}
              </div>
              <div>
                <p className="font-bold text-white text-sm">{staff.name}</p>
                <p className="text-[10px] text-gray-500 uppercase font-bold bg-white/5 px-1.5 py-0.5 rounded w-fit mt-1">
                  {staff.role}
                </p>
              </div>
            </div>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => toggleStatus(staff.id)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase transition-colors ${
                staff.status === "IN"
                  ? "bg-green-500 text-black shadow-[0_0_10px_rgba(34,197,94,0.4)]"
                  : "bg-transparent text-gray-500 border border-white/10"
              }`}
            >
              {staff.status === "IN" ? "Inside" : "Check In"}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// 5. VEHICLE SCREEN
const VehicleScreen: React.FC<ScreenProps> = ({ wrapperClass, paddingTop }) => {
  const [isCalling, setIsCalling] = useState(false);

  return (
    <div className={`${wrapperClass} ${paddingTop} px-6`}>
      <div className="mb-6 lg:mb-8">
        <h4 className="font-bold text-2xl text-white">Vehicle Search</h4>
        <p className="text-gray-500 text-xs">
          Identify wrong parking instantly
        </p>
      </div>

      <div className="bg-[#151515] border border-white/10 rounded-2xl p-4 flex items-center gap-4 mb-6 lg:mb-8">
        <Search className="w-5 h-5 text-gray-400" />
        <div className="flex-1">
          <p className="text-[10px] text-gray-500 uppercase mb-0.5 font-bold tracking-wider">
            Number Plate
          </p>
          <p className="text-xl font-mono text-white tracking-widest">
            MH 02 AB 12__
          </p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
          <ArrowRight className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-[2rem] p-6 relative">
        <div className="absolute top-6 right-6 flex items-center gap-1.5 bg-green-900/20 px-2 py-1 rounded-full border border-green-500/20">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] font-bold text-green-500 uppercase">
            Found
          </span>
        </div>
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-gray-800 to-black rounded-full flex items-center justify-center border border-white/10">
            <Users className="w-6 h-6 lg:w-7 lg:h-7 text-gray-300" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">Rahul Sharma</h3>
            <p className="text-sm text-orange-500 font-medium">
              Flat A-101 • Owner
            </p>
          </div>
        </div>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setIsCalling(true);
            setTimeout(() => setIsCalling(false), 2000);
          }}
          className={`w-full py-4 font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${
            isCalling ? "bg-green-500 text-white" : "bg-white text-black"
          }`}
        >
          {isCalling ? (
            <>
              <PhoneCall className="w-4 h-4 animate-pulse" /> Calling...
            </>
          ) : (
            <>
              <Phone className="w-4 h-4 fill-black" /> Call Owner
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default GateKeepingSection;
